import { strategySelect } from 'src/app/features/warehouse/components/bodies/all-sessions-into-wrap/all-sessions-into-wrap.component';
import { CurrentDynaData, DynaConfActionType, DynaCrudRequest, selectCurrentDynaData, State } from 'src/app/store/shared/shared.reducers';
import { DynacrudApi, OperationType, SearchRequest, Sort, SortDirection } from '../../models/dynacrud';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, map, Observable, Subject, switchMap, take, tap, throwError } from 'rxjs';
import { SelectionMode, TableActionType, TableConfig, TableEvent } from '../../models/table-config';
import { HostColumnEditService } from 'src/app/shared/services/host-column-edit.service';
import { TableData, TableSelection, TableStatus } from '../table/table/table.component';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { DynacrudApiWrapper, DynaCrudService } from '../../api/dynacrud-api';
import { LayoutActionType } from 'src/app/store/layout/layout.reducers';
import { environment } from 'src/environments/environment';
import { DialogService } from 'src/app/shared/services/dialog.service';
import { ProductStructure, TransferType } from 'src/app/shared/models/entities';
import { BaseAction } from 'src/app/store/store.actions';
import { ActivatedRoute, Router } from '@angular/router';
import { _isNumberValue } from '@angular/cdk/coercion';
import { RouteConf } from '../../models/route-config';
import { select, Store } from '@ngrx/store';
import { SubSink } from 'subsink';
import * as _ from 'lodash';
import { Location } from '@angular/common';
import { logFilterDiag } from 'src/app/shared/utils/filter-leak-diag';
import { ListStateService } from '../../services/list-state.service';

export interface DefaultListComponentConf {
  tableConfig?: TableConfig;
  first?: boolean;
  searchRequestDefault?: SearchRequest;
  displayedColumns?: string[];
  baseRoute?: string;
  isTemplate?: boolean;
  loadOnInit?: boolean;
  apiUrl?: string;
}


export enum TypeTable {
  'NORMAL' = 'NORMAL',
  'EDIT' = 'EDIT',
}

@Component({
  selector: 'app-default-list',
  templateUrl: './default-list.component.html',
  styleUrls: ['./default-list.component.scss']
})
export class DefaultListComponent implements OnInit, OnDestroy {
  private dynaCrudRequestSub: Subject<DynaCrudRequest> = new Subject<DynaCrudRequest>();
  private dataSourcetSub: Subject<TableData> = new Subject<TableData>();
  readonly dataSource: Observable<TableData> = this.dataSourcetSub.asObservable();
  private lastDynaCrudRequest!: DynaCrudRequest;
  /**
   * Whether the search this component last ISSUED came back as a failure.
   *
   * `lastDynaCrudRequest` is stamped BEFORE the request goes out and is never unstamped, so on its
   * own it records "what was sent", not "what is on screen". Once a failed search is swallowed
   * inside the `switchMap` (see that `catchError`), the two stop agreeing: the stamp names a
   * request whose answer never arrived, and `isDynaCrudRequestChange` then reads every later
   * emission carrying the SAME criteria as "nothing changed" -- so the one gesture that should
   * obviously work after a 500, asking for the same thing again, is the one gesture that cannot.
   * The list sits on empty rows until the user alters a filter or leaves the route.
   *
   * Cleared at the top of every search rather than on success, so the licence is spent by the
   * search it licenses rather than left open.
   *
   * THE FLAG IS NOT THE WHOLE LICENCE, AND ON ITS OWN IT LOOPED. It is re-armed by the retry's own
   * failure, so with a request that fails PERMANENTLY -- a poisoned slot, a filter the endpoint
   * refuses, a missing structural parameter -- "one retry per emission" is one retry per emission
   * FOREVER: every later emission of `selectCurrentDynaData` re-fires the same 500 and the
   * interceptor raises another toast. The emissions are not rare, and one of them is this
   * component's own: the search pipeline clears a non-empty selection from inside the `switchMap`,
   * which writes the slot and comes straight back. A click anywhere on the list then cost a
   * request and a toast apiece.
   *
   * So the flag says "the rows on screen do not match the stamp", and {@link slotUnchanged} says
   * "this emission is the user asking again" -- a retry needs both. A selection, a row highlight
   * or an item list moving is not a request to re-search, and an emission that changed none of
   * them is exactly what an identical re-dispatched `FilterChange` looks like from here. Bounded
   * without capping anything: nothing the app does by itself can license a retry, and a user
   * pressing Search can do it as often as they like.
   */
  private lastSearchFailed = false;
  private service!: DynaCrudService<any>;
  private subs = new SubSink();

  constructor(
    private location: Location,
    private router: Router,
    private _activ: ActivatedRoute,
    private dynacrudService: DynacrudApiWrapper,
    private store: Store<State>,
    private dialog: DialogService,
    private hostServiceColumnSingle: HostColumnEditService,
    @Optional() private listState?: ListStateService,
  ) { }

  @HostListener('click', ['$event']) onClick(btn: MouseEvent) {
    // `rows[0]` is EITHER shape: single-select puts the raw row there, while the multiple /
    // multipleWithBaseRoute paths push `{row, selected}` wrappers -- hence the `.row` fallback.
    // It is optional-chained because the raw-row case has no `.row` at all, so a row whose own
    // `id` is falsy (null, 0, '') used to throw straight out of this host listener on every
    // click. That is not hypothetical: the grouped dashboard's aggregate row carries a null id
    // by design, and any list with an id of 0 would do the same.
    if ((this.conf.tableConfig?.selectionMode != SelectionMode.multiple) && this.selection && this.selection.rows && this.selection.rows[0] && (this.selection.rows[0].id || this.selection.rows[0].row?.id)) {

      const url = this.router.serializeUrl(
        this.router.createUrlTree([`${this.conf.baseRoute}${this.selection.rows[0].id || this.selection.rows[0].row?.id}`])
      );

      this.hotClick = btn.ctrlKey || btn.metaKey;
      if (btn.ctrlKey || btn.metaKey) {
        window.open(url);
      }
    }
  }


  get tableConfig(): TableConfig {
    return {
      ...this.conf.tableConfig || {
        actions: [
          { type: TableActionType.GLOBAL, icon: 'add', event: 'add', label: 'common.actions.add' },
          { type: TableActionType.RECORD, icon: 'edit', event: 'edit', label: 'common.actions.edit' },
          { type: TableActionType.RECORD, icon: 'delete', event: 'delete', label: 'common.actions.delete' },
        ]
      }
    };
  }

  get displayedColumns(): string[] {
    return this.conf.displayedColumns || ['id'];
  }

  @Input()
  routeConf: RouteConf | undefined;

  @Input()
  conf!: DefaultListComponentConf;

  @Input()
  typeTable!: TypeTable;

  @Input()
  typeTableViewCondition: ProductStructure | undefined;

  @Input() strategySelect: strategySelect | undefined;

  @Input() notInputPage!: boolean;


  @Input() onlyCheck!: boolean;

  @Input() normalSearchText!: boolean;


  @Input()
  getRowTable: boolean | undefined;

  /** Emits the total matching element count on every data load (from the response meta). */
  @Output()
  totalElementsChange = new EventEmitter<number>();

  /**
   * WCAG 4.1.3. What the list's live region says: the number of results the last completed search
   * returned, or `null` before one has. The region itself is unconditional in the template (see the
   * comment there) and this is the only thing that changes in it.
   *
   * It is the TOTAL rather than the rows on this page, because that is the answer to the question a
   * search asks. For a grouped list it counts groups, which is what the paginator shows there too.
   */
  announcedTotal: number | null = null;

  dataExtra: { [key: string]: any[] } = {};
  private lastDynaData: CurrentDynaData | undefined = undefined;
  transFerType = TransferType;
  selection: any;
  hotClick = false;
  currentSearch!: DynaCrudRequest;

  select(tableSelection: TableSelection) {

    this.selection = tableSelection;
    // fixme santiago controlar la seleccion ... porcata per cablare il routeconf
    // FIXME: si deve aggiungere il controllo anche per quelli routeConf dove ho aggiunto [trans e unload]

    if ((!(this.routeConf?.code == this.transFerType.TRANS || this.routeConf?.code == this.transFerType.UNLOAD))) {
      this.store.dispatch(new BaseAction(DynaConfActionType.SelectionChange, tableSelection || { all: false, rows: [] }, this.routeConf?.code));
    }
    // per config table
    const selectionMode = this.conf?.tableConfig?.selectionMode || SelectionMode.single;

    const isTrusted = tableSelection.rows.length > 0 && this.conf.baseRoute && ((selectionMode === SelectionMode.single) || (selectionMode === SelectionMode.multipleWithBaseRoute));

    if (isTrusted) {
      setTimeout(() => {
        if (!this.hotClick) {
          if (!tableSelection.rows[0]?.not) {

            let toGo = `${this.conf.baseRoute}${tableSelection.rows[0]?.id || tableSelection.rows[0]?.row?.id}`;

            if (!tableSelection.ctrlKey) {
              toGo = `${this.conf.baseRoute}${tableSelection.rows[tableSelection.rows.length - 1]?.id || tableSelection.rows[tableSelection.rows.length - 1]?.row?.id}`
            }

            this.router.navigateByUrl(toGo).then(() => {
              // let pagination = { pagination: { page: 0, size: 20, }, sort: [], reset: true };
              // //FIXME: LO DEVE FARE SEMPRE ?  PENSIAMOCI AD UN ALTRO MODO GRANDE ( il reset (quello aggiunto) forse mi serve sempre per sapere quando togliere/rimuovere un item intero dal searchRequest ) :)

              // this.store.dispatch(new BaseAction(DynaConfActionType.PaginationSortChange, { ...pagination }));
            });

          }

        }
      }, 0);
    }

  }



  action(event: TableEvent) {
    if (this.conf.baseRoute) {
      if (event.data) {
        if (event.name === 'delete') {
          // FIXME SANTIAGO FINIRE DEFAULT LIST - UNIRE IL ACTION CON LO STATO

          // FIXME SANTIAGO ant-lab/galactus/v2/galactus-ui#10
          const translationKey = this.routeConf?.translationKey ?? this.routeConf?.code;
          const options = {
            title: `dialog.delete.${translationKey}.title`,
            message: `dialog.delete.${translationKey}.message`,
            cancelText: `dialog.delete.${translationKey}.cancel`,
            confirmText: `dialog.delete.${translationKey}.confirm`
          };
          this.dialog.open(options).subscribe(confirmed => {
            if (confirmed) {
              this.service.delete(event.data).subscribe(() => this.dynaCrudRequestSub.next(this.lastDynaCrudRequest));
            }
          });
        } else {
          if (event.name) {
            this.router.navigateByUrl(`${this.conf.baseRoute}${event.name}/${event.data.id}`).then();
          } else {
            this.router.navigateByUrl(`${this.conf.baseRoute}${event.name}`).then();
          }
        }
      } else {
        this.router.navigateByUrl(`${this.conf.baseRoute}${event.name}`).then();
      }
    }
  }

  /**
   * The page the last clamp wrote, until an answer comes back in range; `null` when no clamp is
   * pending. Keyed on the PAGE rather than a flag: a clamped search can be superseded by another
   * out-of-range request before it answers (flat -> grouped, the dashboard header's first
   * FilterChange re-sends the handed-over page), and a boolean then dropped that answer and left an
   * empty page under a non-zero total.
   */
  private pageClampedTo: number | null = null;

  /**
   * A page restored from state (or left behind by data that shrank) can sit past the last page,
   * and the server then answers an empty page with a non-zero total. Re-ask at the last page,
   * through the list's own pagination path (owner code included), keeping the current sort.
   * Only the answer to the page the list clamped TO may end it: if THAT page is still out of range
   * (the data shrank again) stop rather than loop; any other out-of-range answer is clamped again.
   */
  private clampPastLastPage(page?: { length?: number; pageSize?: number; pageIndex?: number }) {
    const total = Number(page?.length);
    const size = Number(page?.pageSize);
    const index = Number(page?.pageIndex);
    if (!(total > 0) || !(size > 0) || !(index >= 0) || index * size < total) {
      this.pageClampedTo = null;
      return;
    }
    if (this.pageClampedTo === index) {
      return;
    }
    const lastPage = Math.ceil(total / size) - 1;
    const pagination = { page: lastPage, size, length: total };
    if (this.listState) {
      this.pageClampedTo = lastPage;
      this.listState.patchPagination(pagination);
      return;
    }
    const ownerCode = this.routeConf?.code;
    if (!ownerCode) {
      return;
    }
    this.pageClampedTo = lastPage;
    // `reset: true` leaves the slot's sort untouched (the reducer overwrites it otherwise, and the
    // sanitised request's sort may carry a tiebreak that must not be persisted).
    this.store.dispatch(new BaseAction(DynaConfActionType.PaginationSortChange, {
      pagination, reset: true, code: ownerCode, srcTag: 'default-list',
    }));
  }

  tableStatusChange(tableStatus: TableStatus) {
    const searchRequest: SearchRequest = {};
    if (tableStatus && tableStatus.sort) {
      searchRequest.sort = tableStatus.sort.map(s => {
        return { field: s.field, direction: SortDirection[s.direction.toUpperCase() as SortDirection] };
      });
    }

    if (tableStatus && tableStatus.page && (tableStatus.page.length != undefined)) {
      searchRequest.pagination = { page: tableStatus.page.pageIndex, size: tableStatus.page.pageSize, length: tableStatus.page.length };
    }

    if (this.listState) {
      this.listState.patchSort(searchRequest.sort ?? []);
      if (searchRequest.pagination) {
        this.listState.patchPagination({
          page: Number(searchRequest.pagination.page),
          size: Number(searchRequest.pagination.size),
          length: searchRequest.pagination.length != null ? Number(searchRequest.pagination.length) : undefined,
        });
      }
    } else {
      // Carry the owning route's code. This emission is deferred twice on its way here
      // (`TableComponent.status` is an async EventEmitter, `paginator.page` is debounced),
      // so it can reduce AFTER a navigation has already switched the active route -- and
      // an anonymous PaginationSortChange writes into whatever slot is active then. That
      // is how a sort taken on the orders/sessions list (`priority`) ended up in the cart
      // slot and broke /v2/stocks. The reducer now drops a dispatch with no owner code.
      const ownerCode = this.routeConf?.code;
      if (!ownerCode) {
        // Dropping the user's sort/paginate gesture is the safe choice (an anonymous write is what
        // broke the cart), but it is invisible from the UI — the table just does not re-sort. The
        // window is small (`init()` binds `routeConf` on the first store emission), so leave a
        // trace rather than a console line: this reaches the leak ring the same diagnostics do.
        logFilterDiag('defaultList.sortDroppedNoOwner', {
          apiUrl: this.conf?.apiUrl,
          sort: (searchRequest?.sort as Sort[] | undefined)?.map(item => item?.field),
          pagination: searchRequest?.pagination,
        });
        return;
      }
      this.store.dispatch(new BaseAction(DynaConfActionType.PaginationSortChange, { ...searchRequest, code: ownerCode, srcTag: 'default-list' }));
    }
  }

  /**
   * Sort fields this list may legitimately send: its own sortable columns (a column's
   * `sortExpression` falls back to its field, then its id) plus whatever its route seeds
   * as a default sort. Returns null when the list declares no columns — there is nothing
   * to check against and guessing would strip legitimate sorts.
   */
  private allowedSortFields(): Set<string> | null {
    const columns = this.conf?.tableConfig?.columnsFields;
    if (!columns || columns.length === 0) {
      return null;
    }
    const allowed = new Set<string>();
    columns.forEach(column => {
      // All three names a column answers to. `sortExpression` is what the table emits, but it
      // FALLS BACK to `field` — so a column that declares an explicit expression would leave its
      // own field name unrecognised, and anything sorting by field name (a filter component, a
      // restored slot written before the expression existed) would be stripped as foreign.
      allowed.add(column.sortExpression);
      allowed.add(column.field);
      allowed.add(column.id);
    });
    [this.routeConf?.searchRequestDefault?.sort, this.conf?.searchRequestDefault?.sort].forEach(sort => {
      (sort ?? []).forEach(item => item?.field && allowed.add(item.field));
    });
    return allowed;
  }

  /**
   * The column set the PREVIOUS sanitise pass ran against. Some lists build their
   * `columnsFields` at runtime (`TableComponent` itself synthesises them from
   * `displayedColumns`, and feature conf components add their own), so an early pass can see an
   * INCOMPLETE set and read a legitimate sort as foreign.
   *
   * The two halves of this guard cost very different things when that happens. Stripping the
   * field from the outgoing request is harmless and reversible — the endpoint is protected either
   * way and the next search re-derives everything. HEALING writes the loss into persisted state,
   * where the user cannot get it back. So they are decoupled: strip on every pass, persist only
   * once the column set has stopped changing (the same signature seen twice in a row).
   */
  private lastSortColumnSignature?: string;

  /**
   * Drop foreign sort fields before they reach the endpoint, and heal the slot that
   * carried them. The slot is persisted, so a leak survives reloads; sending the cleaned
   * sort back into THIS list's own slot is what stops the 500 from returning tomorrow.
   */
  private sanitizeSort(searchRequest: SearchRequest): SearchRequest {
    const sort = searchRequest?.sort as Sort[] | undefined;
    if (!sort || sort.length === 0) {
      return searchRequest;
    }
    const allowed = this.allowedSortFields();
    if (!allowed) {
      return searchRequest;
    }
    // Computed BEFORE the early return below: a pass that drops nothing is still evidence that
    // the column set has settled, so the next one that does drop something may heal straight away.
    const signature = [...allowed].sort().join('|');
    const columnsSettled = signature === this.lastSortColumnSignature;
    this.lastSortColumnSignature = signature;

    const kept = sort.filter(item => !item?.field || allowed.has(item.field));
    if (kept.length === sort.length) {
      return searchRequest;
    }

    logFilterDiag('defaultList.sortSanitized', {
      routeConfCode: this.routeConf?.code,
      apiUrl: this.conf?.apiUrl,
      dropped: sort.filter(item => item?.field && !allowed.has(item.field)).map(item => item.field),
      kept: kept.map(item => item.field),
      // `false` means the columns were still moving, so the drop was applied to this request only
      // and nothing was written back — read it as "not trusted enough to persist yet".
      healed: columnsSettled,
    });

    if (!columnsSettled) {
      return { ...searchRequest, sort: kept };
    }

    // Heal whichever store this list actually reads from, or the bad sort is stripped again on
    // every single search for the life of the feature. A ListStateService list persists under its
    // own `ls.<key>` entry, so it can be poisoned exactly like a shared slot can.
    if (this.listState) {
      this.listState.patchSort(kept);
    } else if (this.routeConf?.code) {
      // This write lands in the slot the search pipeline reads, so it re-emits and the in-flight
      // search is switchMapped away and re-issued once — one aborted request, then the second
      // pass finds nothing to drop and returns the request untouched. Cheaper than leaving a
      // persisted slot that breaks the endpoint on every future visit.
      //
      // Deferred to a microtask because this runs INSIDE the switchMap projection, i.e. while the
      // store is mid-emission: dispatching there re-enters the reducer from a subscriber of the
      // very state it is producing. It converges either way, but NgRx builds state as a `scan`
      // over the action stream and a reentrant dispatch is the fragile shape — a microtask keeps
      // the heal in the same task without nesting it inside the emission.
      //
      // `pagination` is carried through deliberately. The reducer assigns
      // `searchRequest.pagination = action.payload.pagination` unconditionally, so the heal has to
      // name it or it would clear the slot's page size — and the value here IS the slot's own
      // (this `searchRequest` came out of the very slot being healed), so this writes back what is
      // already there. Omitting the key would not help either: the reducer reads
      // `action.payload.pagination`, which is undefined whether the key is absent or explicitly
      // undefined.
      const healed = { pagination: searchRequest.pagination, sort: kept, code: this.routeConf.code, srcTag: 'default-list.sortSanitized' };
      queueMicrotask(() => this.store.dispatch(new BaseAction(DynaConfActionType.PaginationSortChange, healed)));
    }

    return { ...searchRequest, sort: kept };
  }

  private featureCodeNoS(code: string): { value: string, s: boolean } {
    return {
      value: code,
      s: true
    };
  }


  private init(currentDynaData: CurrentDynaData) {

    if (!this.routeConf) {
      const routeConf = currentDynaData.route;

      if (this.conf) {
        if (routeConf?.attributes?.['DefaultListComponent']) {
          this.conf = _.merge(this.conf, routeConf.attributes['DefaultListComponent'] as DefaultListComponentConf);
        }
      } else {
        this.conf = routeConf.attributes?.['DefaultListComponent'] as DefaultListComponentConf;
      }

      this.routeConf = routeConf;


      this.service = this.dynacrudService.getFor((this.conf.apiUrl) || (this.routeConf?.noEndWithS ?
        this.featureCodeNoS(this.routeConf.featureType as string)
        : this.routeConf.featureType) || this.routeConf.code || '', (this.routeConf.params || undefined));

      const currentCrudRequest: DynaCrudRequest = currentDynaData.request || { searchRequest: { filter: {} } };
      const firstTableStatus: TableStatus = { page: { pageIndex: 0, pageSize: 20, length: 0 }, sort: [] };

      if (currentCrudRequest && currentCrudRequest.searchRequest && currentCrudRequest.searchRequest.pagination) {
        firstTableStatus.page.length = 0;
        firstTableStatus.page.pageSize = currentCrudRequest.searchRequest.pagination.size as number;
        firstTableStatus.page.pageIndex = currentCrudRequest.searchRequest.pagination.page as number;
        firstTableStatus.sort = currentCrudRequest.searchRequest.sort as Sort[];

      }


      this.dataSourcetSub.next({ data: [], tableStatus: { ...firstTableStatus } });
      this.dynaCrudRequestSub.pipe(
        switchMap((dynaCrudRequest: DynaCrudRequest) => {

          // FIXME: vedere il searchRequest che passa prima
          const searchRequest: SearchRequest = dynaCrudRequest.searchRequest ?
            dynaCrudRequest.searchRequest :
            { filter: { filterBy: { field: 'obsolete', value: false, operation: OperationType.EQ } } };

          this.lastDynaCrudRequest = _.cloneDeep(dynaCrudRequest);
          // Cleared here, beside the stamp it qualifies: the two are one record of the last search.
          this.lastSearchFailed = false;
          this.store.dispatch(new BaseAction(LayoutActionType.Loading, true));


          // FIXME K3 non deve stare insieme al DynaConf
          if (this.lastDynaData?.routeData?.selection && (this.lastDynaData.routeData.selection.rows.length > 0 || this.lastDynaData.routeData.selection.all)) {
            if (this.routeConf?.code != 'transferType') {
              this.store.dispatch(new BaseAction(DynaConfActionType.SelectionChange, { rows: [], all: false }, this.routeConf?.code));
            }
          }

          if (this.routeConf && this.routeConf.first) {
            // Seed the route's default filter ONLY on a genuinely fresh slot (no restored filter).
            // On a refresh the rehydrated slot already carries the user's saved filter (filterBy);
            // the default (e.g. stock's active/obsolete) must NOT overwrite it — otherwise the list
            // searches with the default while the store still holds the real filter (which is why
            // the summary side-fetch, reading the slot directly, showed the correct one and the
            // list did not). searchRequestDefault seeds only the first-ever visit.
            //
            // GUARDED ON A DEFAULT ACTUALLY EXISTING. Unguarded, this assigned `undefined` for a
            // route that declares `searchRequestDefault` without a `filter` -- so "seed the
            // default" DELETED the request's filter, taking the restored full-text `text` with it
            // and leaving nothing in its place. Inert on today's routes (all four `first` confs
            // declare a filter), which is exactly when a guard is free.
            //
            // CLONED, for the same reason the declared sort below is. `searchRequestDefault` is a
            // MODULE-LEVEL const shared by every visit and every tab, and what is assigned here
            // does not stay local: it is sanitised, kept as `currentSearch`, and handed to
            // `<app-table-edit-rows [filtersOff]>`, which puts it in a PERSISTED set-insert draft.
            // Aliasing it makes the conf reachable — and writable — from all three, so one in-place
            // write corrupts the route for the rest of the page load (the `filter.text = ''` block
            // further down, currently commented out, is exactly such a write). For
            // `ShopDashBoardGroupedRouterConf` that object is the only carrier of
            // `parameters['group by']`, and `@GroupingRequired` answers a search without it with a
            // 500 rather than a clean 400. The `parameters` seeding below cannot save it either:
            // it re-wraps the filter only when one is MISSING, and this conf's filter already
            // carries it.
            if (!searchRequest.filter?.filterBy && this.routeConf.searchRequestDefault?.filter) {
              searchRequest.filter = _.cloneDeep(this.routeConf.searchRequestDefault.filter);
            }
          }

          // THE DECLARED SORT, and it is deliberately OUTSIDE the `first` block above.
          //
          // `searchRequestDefault.sort` used to be read by `allowedSortFields` ALONE, i.e. it
          // declared which fields the sanitiser lets through and applied nothing -- so a list whose
          // default sort is not one of its columns had to seed the value from its own filter/body
          // component as well, and one that did not simply searched with no sort at all. That is a
          // real defect for a GROUPED search: `GROUP BY` with `LIMIT`/`OFFSET` and no `ORDER BY`
          // has no guaranteed row order, so paging can repeat or skip a group.
          //
          // SEEDING IT ONCE DOES NOT FIX THAT, WHICH IS WHY THE CONDITION IS NOT `first`. Three
          // separate mechanisms take the sort away again, and only re-applying survives all three:
          //
          //  1. `first` is mutated to `false` on the module-level `RouteConf` const by the `tap`
          //     below, so it is a once-per-PAGE-LOAD latch. The dashboard header dispatches its
          //     composed `FilterChange` ~800ms after the list's own first search; by then `first`
          //     is already false.
          //  2. Nothing seeded here ever reaches the store: `selectCurrentDynaData` deep-clones
          //     its slot, so `searchRequest` is a copy. `FilterChange` never writes `sort` either,
          //     so search #2 goes out with none.
          //  3. Persisting it into the slot would still not hold. `TableComponent` rebuilds
          //     `sortStatus` out of `columnsFields` alone (`f.sortExpression === s.field`), so a
          //     sort field with NO COLUMN -- which `groupKey` is, by design -- is dropped on the
          //     next render, and the first paginate emits `sort: []` straight back into the slot.
          //
          // Opt-in per route, and `enforceDefaultSort` is the WHOLE of the opt-in: four routes
          // declare a sort purely for the sanitiser, and `users`/`workingMovements` have never had
          // theirs sent to a backend -- applying them all would re-order three unrelated lists by
          // a field nobody has exercised.
          //
          // `first` used to be an OR-arm here and contradicted the paragraph above it: a route that
          // is `first: true` AND declares a sanitiser-only sort would have had that sort applied on
          // the first search of every page load, which is precisely what "not applied to every
          // declaration" rules out. Inert on today's four `first` confs -- only the grouped one
          // declares a `sort`, and it sets `enforceDefaultSort` as well -- which is exactly when
          // the arm is free to drop.
          //
          // APPENDED AS A TIEBREAK, NOT SKIPPED -- and that is the whole of the fix over the first
          // version, which applied the declared sort ONLY to a request carrying none.
          //
          // "A sort the user picked wins" is the right rule for a sort that is a PREFERENCE. This
          // one is not: `groupKey` is what makes a paged `GROUP BY` orderable at all, and the
          // hazard it closes does not care why the sort is there. Every one of the eight columns
          // the grouped dashboard lets a user sort by is NON-UNIQUE across groups -- a status, a
          // priority, a warehouse, a date, a customer name, two money totals -- so the moment the
          // user clicks one, `ORDER BY <non-unique> LIMIT/OFFSET` hands MySQL no total order and
          // paging can repeat or skip a whole group again. The defect was reachable by one click.
          //
          // So the declared sort becomes the LAST key rather than the only one: the user's choice
          // still decides what the list is ordered by, and the declared field only breaks ties
          // between rows the user's choice cannot separate. `sort` is an array the backend turns
          // into an `ORDER BY` list (a two-field sort is what the cart leak sent), so this costs
          // nothing but a second key.
          //
          // Deduplicated by field: a user who sorts by the declared field itself must not send it
          // twice, and the direction they chose is the one that wins. Cloned, because `sanitizeSort`
          // and the reducer both treat the request's `sort` as theirs while this array belongs to a
          // module-level const shared by every visit.
          if (this.routeConf?.enforceDefaultSort && this.routeConf?.searchRequestDefault?.sort?.length) {
            const chosen = (searchRequest.sort as Sort[] | undefined) ?? [];
            const chosenFields = new Set(chosen.map(item => item?.field).filter(field => !!field));
            const tiebreaks = (_.cloneDeep(this.routeConf.searchRequestDefault.sort) as Sort[])
              .filter(item => item?.field && !chosenFields.has(item.field));
            if (tiebreaks.length) {
              searchRequest.sort = [...chosen, ...tiebreaks];
            }
          }

          // STRUCTURAL FILTER PARAMETERS, on every search, for the same class of reason and a
          // worse failure. `filter.parameters` carries what the ENDPOINT requires rather than what
          // the list prefers -- `ShopDashBoardGroupedRouterConf` declares `{'group by': [...]}`,
          // and galactus-core's `@GroupingRequired` answers a search without it with a 500, not a
          // clean 400.
          //
          // `FilterChange` REPLACES the slot's filter wholesale, so a parameter seeded once is
          // gone the first time any filter component dispatches. `DashboardHeaderComponentsComponent`
          // re-stamps it on its own dispatches, which covers the searches IT causes -- but not the
          // list's own, which runs first and runs whether or not that header ever gets as far as
          // dispatching (its `search()` sits behind a warehouse resolution AND a `statusCount`
          // round trip). Leave the first visit before both land and the slot persists with a bare
          // `{}` filter; on the next visit `first` is false, nothing re-seeds, and the grouped
          // search 500s.
          //
          // A route that declares no `parameters` is untouched: the key is never written, which
          // matters because `_.isEqual` counts keys and an `undefined`-valued one would read as a
          // change and cost every list a duplicate search.
          const declaredParameters = this.routeConf?.searchRequestDefault?.filter?.parameters;
          if (declaredParameters && !searchRequest.filter?.parameters) {
            searchRequest.filter = { ...(searchRequest.filter || {}), parameters: _.cloneDeep(declaredParameters) };
          }

          // Last line of defence: never send a sort field this list does not own. A slot
          // is persisted, so one leaked sort (see tableStatusChange) otherwise breaks the
          // endpoint on every visit until appVersion changes -- the backend answers
          // `UnknownPathException: Could not resolve attribute '<field>'` with a 500.
          const sanitizedRequest = this.sanitizeSort(searchRequest);

          // Sanitised, and BEFORE anything else reads it. `currentSearch` is not just a
          // record of the last search: the template hands it to `<app-table-edit-rows
          // [filtersOff]>`, which puts it in the set-insert DRAFT payload. A draft is
          // persisted and replayed on restore, so assigning the raw request here would
          // store the foreign sort and seed it straight back into the slot the sanitiser
          // just cleaned -- the one path by which a stripped sort could still come back.
          this.currentSearch = { ...dynaCrudRequest, searchRequest: sanitizedRequest };

          // Diagnostic: when this default-list overrides apiUrl (custom
          // endpoint like 'sessions/{id}/movements'), record the search so we
          // can correlate the apiUrl with the route.code whose dynaData slot
          // fed the searchRequest. A mismatch is the filter-leak signature.
          // Narrow surface — fires only for apiUrl-override lists, not the
          // hundreds of normal lists.
          if (this.conf?.apiUrl) {
            logFilterDiag('defaultList.search', {
              apiUrl: this.conf.apiUrl,
              routeConfCode: this.routeConf?.code,
              featureType: this.routeConf?.featureType,
              filter: sanitizedRequest?.filter,
              pagination: sanitizedRequest?.pagination,
              // Log sort too: the leak that breaks /sessions/{id}/movements is a
              // foreign `actualPrice` sort, invisible to the filter-only diag.
              sort: sanitizedRequest?.sort,
            });
          }

          return this.service.search(sanitizedRequest).pipe(
            distinctUntilChanged(),
            tap(() => this.routeConf && this.routeConf.first != undefined ? this.routeConf.first = false : ''),
            // CAUGHT INSIDE THE switchMap, SO THE LIST SURVIVES ITS OWN FAILED SEARCH.
            //
            // The outer `catchError` below re-throws, which errors the subscription on
            // `dynaCrudRequestSub` -- a Subject, so there is nothing to resubscribe it. One failed
            // search therefore used to end the search pipeline for the REST OF THE COMPONENT'S
            // LIFE: every later filter, sort, paginate and explicit refresh reduced into the slot,
            // emitted, and reached a dead subscriber, so the list sat on whatever rows it had with
            // no way back short of a navigation. A 500 is an ordinary thing for a backend to
            // answer -- a foreign sort field, a filter the endpoint refuses, a structural parameter
            // missing -- and none of them should cost the screen its ability to search again.
            //
            // Nothing user-facing is lost by swallowing it here: `SharedHttpInterceptor` already
            // toasts the failure (`common.error.httpStatusWithMessage`), and this subscription has
            // no error handler of its own -- the error's only other destination was Angular's
            // global `ErrorHandler`.
            //
            // Both loading flags are cleared, not just `Loading`. The outer handler dropped only
            // that one, so a search that failed while a full-screen block was up left the block up.
            catchError(err => {
              // See `lastSearchFailed`: swallowing the failure keeps the pipeline alive, and
              // without this the SAME criteria could never be asked for again -- `lastDynaCrudRequest`
              // still names the request that failed, so the retry reads as "nothing changed" and is
              // dropped by the gate in ngOnInit. The one obvious reaction to a 500 has to work.
              this.lastSearchFailed = true;
              this.store.dispatch(new BaseAction(LayoutActionType.Loading, false));
              this.store.dispatch(new BaseAction(LayoutActionType.LoadingBlock, false));
              console.error('[default-list] search failed for route', this.routeConf?.code, err);
              return EMPTY;
            }));

        }),
        map((data: DynacrudApi<any[]>) => {
          // Flip flag to show that loading has finished.

          const tableStatusRet: TableStatus = { ...firstTableStatus };

          if (data && data.meta && data.meta.pagination) {
            tableStatusRet.page.length = data.meta.pagination.totalElements as number;
            tableStatusRet.page.pageSize = data.meta.pagination.pageSize as number;
            tableStatusRet.page.pageIndex = data.meta.pagination.currentPage as number;
            tableStatusRet.sort = data.meta.sort as Sort[];
          }

          // WCAG 4.1.3. What the list's live region will say. Set HERE, off the response itself,
          // rather than in the `subscribe` below off `tableStatus` -- and that is a correction rather
          // than a preference: `firstTableStatus.page.length` initialises to 0, so after a response
          // that carried no pagination meta it reads as the number zero, which cannot be told apart
          // from a genuine "no results". A fallback keyed on it therefore never fired, and an
          // endpoint answering without meta announced "no results" over rows that were on screen.
          // The response's own meta is the only unambiguous source.
          //
          // This operator only runs for a search that SUCCEEDED (a failure returns EMPTY from the
          // switchMap above), so a 500 announces nothing rather than stating as fact a count nobody
          // measured.
          //
          // Known limit, stated rather than worked around: two searches returning the same count
          // leave the region's text identical, and an unchanged text is not re-announced. Padding the
          // string to force a DOM change is the usual trick and it reads the padding aloud on some
          // screen readers, so a repeat of the same answer is left silent.
          this.announcedTotal = (data?.meta?.pagination?.totalElements as number | undefined)
            ?? data?.data?.length
            ?? null;

          return { data: data.data, tableStatus: tableStatusRet } as TableData;
        }),
        // A BACKSTOP ONLY. Every search failure is now handled inside the switchMap above, so the
        // one thing that can still reach here is a throw from the `map` -- a malformed response
        // shape rather than a failed request. Left re-throwing deliberately: that is a bug in this
        // component, not a backend answer, and it should not be quietly absorbed the way a 500 is.
        catchError(err => {
          this.store.dispatch(new BaseAction(LayoutActionType.Loading, false));
          this.store.dispatch(new BaseAction(LayoutActionType.LoadingBlock, false));
          return throwError(err);
        })
      ).subscribe((ret) => {

        this.store.dispatch(new BaseAction(LayoutActionType.Loading, false));
        this.store.dispatch(new BaseAction(LayoutActionType.LoadingBlock, false));

        // cablato la chiamata only here ? chiama dal cache, mettere la validazione in un metodo e poi controlla se lo fa generico. (LO FA)

        if (currentDynaData.route.code == TransferType.TRANS || currentDynaData.route.code == TransferType.UNLOAD || currentDynaData.route.code == TransferType.products_setInsert) {
          this.dynacrudService.getFor('warehouse').search({
            filter: {
              filterBy: {
                operation: OperationType.AND,
                filters: [
                  { field: 'obsolete', value: 'false', operation: OperationType.EQ },
                  { field: 'virtual', value: 'false', operation: OperationType.EQ }
                ]
              }
            }
          })
            .pipe(map(d => d.data), take(1), debounceTime(environment.defaultDebounceTime)).subscribe(dataWarehouses => {

              if (dataWarehouses && dataWarehouses.length > 0 && ret && ret.data.length > 0) {
                ret.data.forEach((element: any) => {
                  Object.defineProperty(element, 'warehouses', { value: dataWarehouses, writable: false, configurable: true, enumerable: true });
                });


                this.dataExtra['warehouses'] = dataWarehouses;
              }
            });
        }


        this.clampPastLastPage(ret?.tableStatus?.page);

        this.dataSourcetSub.next(ret as TableData);
        this.totalElementsChange.emit(ret?.tableStatus?.page?.length ?? 0);
      });

    }

  }


  newPage(tableSelection: { rows: { selected: boolean, row: any }[], all?: boolean, ctrlKey?: boolean }) {

    if (tableSelection && tableSelection.rows && tableSelection.rows.length > 0 && !tableSelection.rows[0].selected) {
      if (this.conf?.baseRoute && tableSelection && tableSelection.rows && tableSelection.rows.length > 0) {
        const urlActual = this.location.prepareExternalUrl(this.location.path());
        const enlaceTemporal = document.createElement('a');
        const enlaceTemporal2 = document.createElement('a');
        enlaceTemporal2.href = urlActual
        const baseRuta = enlaceTemporal2.protocol + '//' + enlaceTemporal2.host;

        enlaceTemporal.href = `${baseRuta + this.conf?.baseRoute + (tableSelection.rows[0] as any).id}`;
        enlaceTemporal.target = "_blank";

        if (enlaceTemporal) {
          setTimeout(() => {
            (enlaceTemporal).click();
          }, 0);
          return;
        }
      }
    }
  }


  ngOnInit(): void {

    this.dataExtra = {};
    const first = true;

    this.subs.add(
      this.store.pipe(select(selectCurrentDynaData), debounceTime(environment.defaultDebounceTime), distinctUntilChanged()).subscribe(currentDynaData => {
        if (currentDynaData && currentDynaData.route) {
          // cablo per sort e code routeconfigcode
          this.init(currentDynaData);

          // READ-SIDE LEAK GUARD: selectCurrentDynaData returns dynaData[active route.code].
          // Once init() has bound this list to its own route (first emission), ignore any
          // emission whose active route is a DIFFERENT feature. Without this, a list still
          // alive during a fast navigation reads another route's slot and sends it to its own
          // frozen endpoint (e.g. the reports order.* filter -> /v2/stocks). This is the
          // read-side dual of the FilterChange write-side guard in the reducer.
          if (this.routeConf && currentDynaData.route.code && this.routeConf.code !== currentDynaData.route.code) {
            return;
          }

          // if (this.normalSearchText) {

          //   if (currentDynaData && currentDynaData.request && currentDynaData.request.searchRequest && currentDynaData.request.searchRequest.filter
          //     && currentDynaData.request.searchRequest.filter.text) {
          //     if (this.normalSearchText && this.currentSearch?.searchRequest?.filter?.text) {

          //       currentDynaData.request.searchRequest.filter.text = '';

          //     }

          //   }
          // }

          const featureCode: string | undefined = this.routeConf ? this.routeConf.code : undefined;

          if (this.listState) {
            // --- Service-driven path ---
            // Route/service setup is done by init() above; searchRequest is
            // sourced from listState.state$, NOT from the global slot.
            this.lastDynaData = _.cloneDeep(currentDynaData);
          } else {
            // --- Legacy global-slot path (unchanged) ---
            if (featureCode) {

              const dynaCrudRequest: DynaCrudRequest = currentDynaData.request || { searchRequest: { filter: {} } };
              if ((dynaCrudRequest || this.conf.loadOnInit == null || !this.conf.loadOnInit)) {

                // FIXME K3 di seguito una porcata fino a cambiamento della gestione stato ( selection non può stare in dynadata)

                // An EXPLICIT reload. `Refresh` changes nothing else a consumer can name -- see
                // DynaConf.refreshToken -- so without this a successful delete left its rows on
                // screen, because Refresh and the highlight clear arrive as ONE debounced emission.
                const isRefresh = currentDynaData?.refreshToken !== this.lastDynaData?.refreshToken;
                const isDynaCrudRequestChange = !_.isEqual(this.lastDynaCrudRequest, dynaCrudRequest);
                const isNotDynaData = !this.lastDynaData;
                // NOTHING IN THE SLOT MOVED -- the second half of the retry licence, see
                // `lastSearchFailed`. The three fields are `FeatureData`'s own, compared one by one
                // rather than as a whole `currentDynaData`: `route` is a module-level conf carrying
                // COMPONENT CLASSES, and `lodash` does not clone a function, so a whole-object
                // compare against the clone below would be false on every emission and would
                // silently retire the retry altogether.
                const slotUnchanged = !isNotDynaData
                  && _.isEqual(currentDynaData?.request, this.lastDynaData?.request)
                  && _.isEqual(currentDynaData?.data, this.lastDynaData?.data)
                  && _.isEqual(currentDynaData?.routeData, this.lastDynaData?.routeData);
                this.lastDynaData = _.cloneDeep(currentDynaData);
                // A SEARCH NEEDS A REASON: the first accepted emission, an explicit reload, a
                // request that actually differs from the one already searched, or a retry after a
                // search that failed. Those four, and nothing else.
                //
                // There used to be a fourth, and it was self-contradictory: "nothing changed"
                // (`!isDynaCrudRequestChange && !isSelectionChange && !isRowSelectChange`) also
                // searched. `lastDynaCrudRequest` records what was actually SEARCHED, so that clause
                // fired precisely when the answer was already on screen -- and every emission that
                // touched only `routeData` went through it, which is why a selection had to be
                // excluded by hand, then a row highlight after it, each one found as its own bug.
                //
                // Its cost was a second IDENTICAL search on every load of every order list: the
                // header composes its filter ~800ms after the list's own first search, dispatches
                // FilterChange, the composed request comes out deep-equal to what was restored, and
                // the list searched it again -- far too late for the switchMap to cancel anything,
                // so both requests completed and the list repainted. Measured from the browser:
                // `isDynaCrudRequestChange: false` on the second one.
                //
                // Nothing needs it. `isNotDynaData` covers the first emission -- the route guard
                // above returns BEFORE `lastDynaData` is assigned, so the first ACCEPTED emission
                // still counts as the first -- and an explicit reload has carried its own token
                // since `Refresh` was given one. A write that touches only `routeData` now needs no
                // exclusion of its own, because it no longer reaches a clause that searches.
                //
                // THE FOURTH IS A RETRY, AND IT TAKES TWO CONDITIONS. `lastSearchFailed` is the
                // one case where the recorded request and the rows on screen disagree -- see that
                // field -- so without it a failed search could never be re-issued with the SAME
                // criteria, which is the first thing anyone tries after a 500. `slotUnchanged` is
                // what keeps that from becoming a retry on EVERY emission while the failure
                // persists: a selection or a row highlight moving is not the user asking again.
                if (isNotDynaData || isRefresh || isDynaCrudRequestChange || (this.lastSearchFailed && slotUnchanged)) {
                  this.dynaCrudRequestSub.next(dynaCrudRequest);
                }

              }
            }
          }

          if ((featureCode === this.transFerType.TRANS || featureCode === this.transFerType.UNLOAD)) {


            this.subs.add(
              this._activ.paramMap.pipe(distinctUntilChanged()).subscribe(idS => {
                const idSession: number = Number.parseInt(idS.get('id') as string);
                if (idSession) {
                  if (_isNumberValue(idSession)) { // true == 1, false == 1s
                    this.hostServiceColumnSingle._newIdSession.next({ id: idSession, code: featureCode });
                  }
                } else {
                  this.hostServiceColumnSingle._newIdSession.next({ id: undefined, code: null });
                }
              })
            );
          }
        }
      }
      ));

    if (this.listState) {
      // When a ListStateService is injected, drive the search pipeline from
      // service state instead of the global store slot.
      this.subs.add(
        this.listState.state$.pipe(
          debounceTime(environment.defaultDebounceTime),
          distinctUntilChanged(),
        ).subscribe(state => {
          const dynaCrudRequest: DynaCrudRequest = {
            searchRequest: {
              filter: state.filter ?? {},
              sort: state.sort,
              pagination: state.pagination,
            },
          };
          this.dynaCrudRequestSub.next(dynaCrudRequest);
        })
      );
    }

    const emptySelection: TableSelection = { rows: [], all: false };
    if (this.routeConf?.code != 'transferType') {
      this.store.dispatch(new BaseAction(DynaConfActionType.SelectionChange, emptySelection, this.routeConf?.code));
    }
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

}
